import { useEffect, useState } from 'react';
import {
  NEWS_PAGE_SIZE,
  newsQuery,
  publicNewsApi,
} from '@/features/publicsite/news/api/publicNewsApi';
import type { NewsListing } from '@/features/publicsite/types/news/NewsListing';
import type { NewsPageResponse } from '@/features/publicsite/types/news/NewsPageResponse';
import { useLanguage } from '@/i18n/LanguageContext';

/** One page of /stiri for the filters in `params`, and whether what is on screen answers them. */
export function useNewsListing(params: URLSearchParams, page: number): NewsListing {
  const { language } = useLanguage();
  const [data, setData] = useState<NewsPageResponse | null>(null);
  // The request as a string, so the effect below re-runs when a filter changes and not when the
  // URL is rewritten with the same filters in another order.
  const request = newsQuery(params, page);

  // Which request `data` answers. Until the answer to the current one arrives, the list on screen
  // belongs to the previous filters: it stays (a blank page between two lists is worse) but the
  // count and the empty state wait — otherwise picking a category briefly announced the old list's
  // "11 de rezultate" over the old list, as if the filter had matched everything.
  const [answered, setAnswered] = useState<string | null>(null);
  const fresh = answered === request;

  useEffect(() => {
    let live = true;
    publicNewsApi
      .page(request)
      .then((r) => {
        if (!live) return;
        setData(r);
        setAnswered(request);
      })
      .catch(() => {
        // A failed fetch (rate limit, transient network error) must not leave the list stuck
        // in its loading state forever — degrade to the empty state rather than hang.
        if (!live) return;
        setData({ items: [], page, size: NEWS_PAGE_SIZE, totalItems: 0, totalPages: 0 });
        setAnswered(request);
      });
    return () => {
      live = false;
    };
  }, [request, page, language]);

  return { data, fresh };
}
